import { Fragment, useEffect, useState } from "react";
import useAxios from "../../../hook/useAxios";
import { Department } from "../../../type/department";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBuilding, faInbox, faUsers } from "@fortawesome/pro-light-svg-icons";
import { t } from "i18next";
import { TableCell } from "@mui/material";
import { useSelector } from "react-redux";
import { donorsSponsorsReportURL } from "../../../store";
import { DonorsParticipantsSummery } from "../../../type/donorsParticipantSummery";
import ReportTable from "./ReportTable";

interface ReportFilter {
  dateTo: Date;
  dateFromReport: Date | null | "";
  level: string;
  department: Department[] | null;
  community: Department[] | null;
}

const DonorsSponsorsReportsPlantingParticipantsView: React.FC<{
  reportData: ReportFilter;
}> = ({ reportData }) => {
  const [summery, setSummery] = useState<DonorsParticipantsSummery>({
    organizers: {
      communities: 0,
      departments: 0,
      educational: 0,
      governmental: 0,
      NGO: 0,
      other: 0,
      total_institutions: 0,
    },
    participants: {
      communities: 0,
      departments: 0,
      other_participants: 0,
      psa_students: 0,
      psa_tutors: 0,
      school_students: 0,
      school_tutors: 0,
      total_participants: 0,
    },
  });
  const url = useSelector(donorsSponsorsReportURL);

  const { response: participantSummeryResponse } = useAxios(
    process.env.REACT_APP_API_URL +
      `/api/admin/reports/donors/planting_participants/summery${url}`,
    "GET",
    true,
    "",
    true,
    false
  );

  useEffect(() => {
    if (participantSummeryResponse) {
      setSummery(participantSummeryResponse.data);
    }
  }, [participantSummeryResponse]);
  return (
    <div className="flex flex-col p-6 bg-ph-white">
      <h1 className="font-semibold text-default-text text-[14px] mb-5">
        {t("Reports.Planting participants & organizers")}
      </h1>
      <div className="py-4 px-5 bg-ph-bg-white">
        <p className="text-ph-dark-gray text-sm">
          {t("PlantingFilter.Participants")}
        </p>
      </div>

      {summery.participants.total_participants > 0 && (
        <Fragment>
          <div className="flex justify-center sm:justify-start mb-5 border-[1px] border-ph-input border-opacity-100 p-4">
            {reportData.level !== "L" ? (
              <FontAwesomeIcon
                className="text-ph-gray w-[25px] h-[20px] my-[11px] mr-[16px]"
                icon={faBuilding}
              />
            ) : (
              <FontAwesomeIcon
                className="text-ph-gray w-[25px] h-[20px] my-[11px] mr-[16px]"
                icon={faUsers}
              />
            )}
            <div>
              <p className="text-sm text-ph-gray">
                {reportData.level !== "L"
                  ? t("Navbar.Departments")
                  : t("Community.Communities")}
              </p>
              <p className="text-sm text-default-text font-medium">
                {reportData.level !== "L"
                  ? summery?.participants.departments
                  : summery?.participants.communities}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap text-center sm:text-left mb-5 border-[1px] border-ph-input border-opacity-100 p-4">
            <div className="w-full sm:w-[280px] mb-7">
              <p className="text-sm text-ph-gray">
                {t("Statistics.Total participants")}
              </p>
              <p className="text-sm text-default-text font-medium">
                {summery?.participants.total_participants}
              </p>
            </div>
            <div className="w-full sm:w-[280px] mb-7">
              <p className="text-sm text-ph-gray">
                {t("Workshops.PSA students")}
              </p>
              <p className="text-sm text-default-text font-medium">
                {summery?.participants.psa_students}
              </p>
            </div>
            <div className="w-full sm:w-[280px] mb-7">
              <p className="text-sm text-ph-gray">
                {t("Workshops.School students")}
              </p>
              <p className="text-sm text-default-text font-medium">
                {summery?.participants.school_students}
              </p>
            </div>
            <div className="w-full sm:w-[280px] mb-7">
              <p className="text-sm text-ph-gray">
                {t("Workshops.PSA tutors")}
              </p>
              <p className="text-sm text-default-text font-medium">
                {summery?.participants.psa_tutors}
              </p>
            </div>
            <div className="w-full sm:w-[280px] mb-7">
              <p className="text-sm text-ph-gray">
                {t("Workshops.School teachers")}
              </p>
              <p className="text-sm text-default-text font-medium">
                {summery?.participants.school_tutors}
              </p>
            </div>
            <div className="w-full sm:w-[280px] mb-7">
              <p className="text-sm text-ph-gray">
                {t("Workshops.Other participants")}
              </p>
              <p className="text-sm text-default-text font-medium">
                {summery?.participants.other_participants}
              </p>
            </div>
          </div>
        </Fragment>
      )}

      {reportData.level === "N" &&
        summery.participants.total_participants === 0 && (
          <p className="text-center font-medium text-2xl text-default-text py-10">
            <FontAwesomeIcon className="mr-4" icon={faInbox} />
            {t("Message.No results found")}
          </p>
        )}

      {reportData.level !== "N" && (
        <ReportTable
          url={`donors/planting_participants/participants_list${url}`}
          titles={[
            reportData.level === "D"
              ? { name: t("Plantings.Department"), value: "department" }
              : { name: t("Community.Community"), value: "community" },
            {
              name: t("Statistics.Total participants"),
              value: "total_participants",
            },
            { name: t("Workshops.PSA students"), value: "psa_students" },
            { name: t("Workshops.School students"), value: "school_students" },
            { name: t("Workshops.PSA tutors"), value: "psa_tutors" },
            { name: t("Workshops.School teachers"), value: "school_tutors" },
            {
              name: t("Workshops.Other participants"),
              value: "other_participants",
            },
          ]}
          renderItem={(participantListItem) => (
            <Fragment>
              {reportData.level === "D" ? (
                <TableCell align="left">
                  {participantListItem.department}
                </TableCell>
              ) : (
                <TableCell align="left">
                  {participantListItem.community}
                </TableCell>
              )}
              <TableCell align="left">
                {participantListItem.total_participants}
              </TableCell>
              <TableCell align="left">
                {participantListItem.psa_students}
              </TableCell>
              <TableCell align="left">
                {participantListItem.school_students}
              </TableCell>
              <TableCell align="left">
                {participantListItem.psa_tutors}
              </TableCell>
              <TableCell align="left">
                {participantListItem.school_tutors}
              </TableCell>
              <TableCell align="left">
                {participantListItem.other_participants}
              </TableCell>
            </Fragment>
          )}
        />
      )}

      <div className="py-4 px-5 bg-ph-bg-white">
        <p className="text-ph-dark-gray text-sm">
          {t("Reports.Organizers(Institutions)")}
        </p>
      </div>
      {summery.organizers.total_institutions > 0 && (
        <Fragment>
          <div className="flex justify-center sm:justify-start mb-5 border-[1px] border-ph-input border-opacity-100 p-4">
            {reportData.level !== "L" ? (
              <FontAwesomeIcon
                className="text-ph-gray w-[25px] h-[20px] my-[11px] mr-[16px]"
                icon={faBuilding}
              />
            ) : (
              <FontAwesomeIcon
                className="text-ph-gray w-[25px] h-[20px] my-[11px] mr-[16px]"
                icon={faUsers}
              />
            )}
            <div>
              <p className="text-sm text-ph-gray">
                {reportData.level !== "L"
                  ? t("Navbar.Departments")
                  : t("Community.Communities")}
              </p>
              <p className="text-sm text-default-text font-medium">
                {reportData.level !== "L"
                  ? summery?.organizers.departments
                  : summery?.organizers.communities}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap  text-center sm:text-left mb-5 border-[1px] border-ph-input border-opacity-100 p-4">
            <div className="w-full sm:w-[280px] mb-7">
              <p className="text-sm text-ph-gray">
                {t("Reports.Total Institutions")}
              </p>
              <p className="text-sm text-default-text font-medium">
                {summery?.organizers.total_institutions}
              </p>
            </div>
            <div className="w-full sm:w-[280px] mb-7">
              <p className="text-sm text-ph-gray">
                {t("Reports.Governmental")}
              </p>
              <p className="text-sm text-default-text font-medium">
                {summery?.organizers.governmental}
              </p>
            </div>
            <div className="w-full sm:w-[280px] mb-7">
              <p className="text-sm text-ph-gray">{t("Reports.Educational")}</p>
              <p className="text-sm text-default-text font-medium">
                {summery?.organizers.educational}
              </p>
            </div>
            <div className="w-full sm:w-[280px] mb-7">
              <p className="text-sm text-ph-gray">NGO</p>
              <p className="text-sm text-default-text font-medium">
                {summery?.organizers.NGO}
              </p>
            </div>
            <div className="w-full sm:w-[280px] mb-7">
              <p className="text-sm text-ph-gray">{t("Plantings.Other")}</p>
              <p className="text-sm text-default-text font-medium">
                {summery?.organizers.other}
              </p>
            </div>
          </div>
        </Fragment>
      )}

      {reportData.level === "N" &&
        summery.organizers.total_institutions === 0 && (
          <p className="text-center font-medium text-2xl text-default-text py-10">
            <FontAwesomeIcon className="mr-4" icon={faInbox} />
            {t("Message.No results found")}
          </p>
        )}

      {reportData.level !== "N" && (
        <ReportTable
          url={`donors/planting_participants/organizers_list${url}`}
          titles={[
            reportData.level === "D"
              ? { name: t("Plantings.Department"), value: "department" }
              : { name: t("Community.Community"), value: "community" },
            {
              name: t("Reports.Total Institutions"),
              value: "total_institutions",
            },
            {
              name: t("Reports.Governmental"),
              value: "governmental_institutions",
            },
            {
              name: t("Reports.Educational"),
              value: "educational_institutions",
            },
            { name: "NGO", value: "ngo_institutions" },
            { name: t("Plantings.Other"), value: "other_institutions" },
          ]}
          renderItem={(departments) => (
            <Fragment>
              {reportData.level === "D" ? (
                <TableCell align="left">{departments.department}</TableCell>
              ) : (
                <TableCell align="left">{departments.community}</TableCell>
              )}
              <TableCell align="left">
                {departments.total_institutions}
              </TableCell>
              <TableCell align="left">
                {departments.governmental_institutions}
              </TableCell>
              <TableCell align="left">
                {departments.educational_institutions}
              </TableCell>
              <TableCell align="left">{departments.ngo_institutions}</TableCell>
              <TableCell align="left">
                {departments.other_institutions}
              </TableCell>
            </Fragment>
          )}
        />
      )}
    </div>
  );
};

export default DonorsSponsorsReportsPlantingParticipantsView;
